import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const today = () => new Date().toLocaleDateString("sv-SE");
const err = (e) => toast.error(e?.response?.data?.detail || "Błąd");

export function ConvertDialog({ inv, onClose, onDone }) {
  const [type, setType] = useState("VAT");
  const [issue, setIssue] = useState(today());
  const [sale, setSale] = useState(today());
  const [busy, setBusy] = useState(false);
  if (!inv) return null;
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/invoices/${inv.invoice_id}/convert`, { invoice_type: type, issue_date: issue, sale_date: type === "VAT" ? sale : null });
      toast.success(`Wystawiono ${r.data.number}`); onDone();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="convert-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Faktura z proformy {inv.number}</DialogTitle>
          <DialogDescription>Proforma jest opłacona. Wybierz rodzaj faktury, która trafi do KSeF.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {[["VAT", "Faktura końcowa (VAT)", "usługa wykonana lub wykonana w miesiącu wpłaty"], ["ZAL", "Faktura zaliczkowa (ZAL)", "wpłata przed wykonaniem usługi"]].map(([v, l, d]) => (
            <label key={v} className={`flex gap-2 border rounded-md p-3 cursor-pointer ${type === v ? "border-[#193962] bg-[#193962]/5" : "border-border"}`} data-testid={`convert-type-${v}`}>
              <input type="radio" checked={type === v} onChange={() => setType(v)} />
              <span><span className="text-sm font-medium">{l}</span><br /><span className="text-xs text-muted-foreground">{d}</span></span>
            </label>
          ))}
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Data wystawienia</Label><Input type="date" value={issue} onChange={(e) => setIssue(e.target.value)} data-testid="convert-issue-date" /></div>
            {type === "VAT" && <div><Label>Data wykonania usługi</Label><Input type="date" value={sale} onChange={(e) => setSale(e.target.value)} data-testid="convert-sale-date" /></div>}
          </div>
          <Button onClick={go} disabled={busy} className="w-full bg-[#193962] text-white" data-testid="convert-submit">{busy ? "Wystawianie…" : "Wystaw fakturę"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EmailDialog({ inv, onClose, onDone }) {
  const [to, setTo] = useState(inv?.buyer?.email || "");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  if (!inv) return null;
  const go = async () => {
    setBusy(true);
    try { await api.post(`/invoices/${inv.invoice_id}/email`, { to, message: msg }); toast.success(`Wysłano do ${to}`); onDone(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="email-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Wyślij {inv.number} e-mailem</DialogTitle>
          <DialogDescription>Od Bena Carsona, z PDF w załączniku{inv.buyer?.country_code !== "PL" ? " (treść po angielsku)" : ""}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="adres@klienta.com" data-testid="email-to" />
          <Textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Dodatkowa wiadomość (opcjonalnie)" data-testid="email-message" />
          <Button onClick={go} disabled={busy} className="w-full bg-[#193962] text-white" data-testid="email-send">{busy ? "Wysyłanie…" : "Wyślij"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function KsefManualDialog({ inv, onClose, onDone }) {
  const [n, setN] = useState("");
  if (!inv) return null;
  const go = async () => {
    try { await api.post(`/invoices/${inv.invoice_id}/ksef/manual`, { ksef_number: n }); toast.success("Zapisano numer KSeF"); onDone(); } catch (e) { err(e); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="ksef-manual-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Numer KSeF dla {inv.number}</DialogTitle>
          <DialogDescription>Jeśli wgrałeś plik XML tej faktury ręcznie w Aplikacji Podatnika KSeF, wpisz nadany numer. Użyj dokładnie pliku pobranego z CRM — kod QR liczy się z jego zawartości.</DialogDescription>
        </DialogHeader>
        <Input value={n} onChange={(e) => setN(e.target.value)} placeholder="9452337470-RRRRMMDD-XXXXXXXXXXXX-XX" data-testid="ksef-manual-input" />
        <Button onClick={go} className="bg-[#193962] text-white" data-testid="ksef-manual-save">Zapisz</Button>
      </DialogContent>
    </Dialog>
  );
}

export async function downloadFile(path, name) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Nie udało się pobrać pliku"); }
}
